<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <span id="title" class="page-title">{{ page.title }}</span>
        <div class="nd-page-kicker">
          Configure o perfil, acesso clínico e permissões disponíveis para este cargo.
        </div>
      </div>

      <div class="button-group">
        <v-btn type="button" outlined color="primary" elevation="0" @click="goBack">
          <v-icon left size="18">mdi-arrow-left</v-icon>
          Voltar
        </v-btn>
        <v-btn type="button" color="primary" elevation="0" :loading="loadingSave" @click="saveRole">
          <v-icon left size="18">mdi-content-save-outline</v-icon>
          Salvar
        </v-btn>
      </div>
    </header>

    <v-form ref="roleForm" v-model="formValid" lazy-validation>
      <v-card class="nd-section-card">
        <div class="nd-table-toolbar">
          <div>
            <div class="nd-table-toolbar__title">Detalhes do cargo</div>
            <div class="nd-table-toolbar__subtitle">Identificação e vínculo com acesso clínico</div>
          </div>
        </div>

        <div class="nd-form-grid role-form-grid">
          <v-text-field
            v-model="role.name"
            label="Nome do cargo"
            outlined
            dense
            :rules="[rules.required]"
           hide-details="auto"/>

          <div class="role-switch-panel">
            <div>
              <strong>Acesso de dentista</strong>
              <span>Permite acesso ao aplicativo e recursos clínicos.</span>
            </div>
            <v-switch
              v-model="role.dentist"
              color="primary"
              hide-details
              inset
              :ripple="false"
            />
          </div>
        </div>
      </v-card>

      <v-card class="nd-section-card">
        <div class="nd-table-toolbar">
          <div>
            <div class="nd-table-toolbar__title">Permissões</div>
            <div class="nd-table-toolbar__subtitle">Selecione as operações liberadas para o cargo</div>
          </div>

          <span class="permissions-counter">{{ selectedPermissionIds.length }} selecionadas</span>
        </div>

        <div v-if="loadingPermissions" class="permissions-loading">
          <v-progress-linear indeterminate color="primary" />
        </div>

        <div v-else-if="availablePermissions.length === 0" class="nd-empty-state">
          <v-icon size="28" color="#9aa39d">mdi-shield-off-outline</v-icon>
          <span>Nenhuma permissão encontrada</span>
        </div>

        <div v-else class="permissions-grid">
          <section
            v-for="group in availablePermissions"
            :key="group.prefix"
            class="permission-group"
          >
            <header class="permission-group__header">
              <div>
                <strong>{{ group.title }}</strong>
                <span>{{ group.permissions.length }} permissões</span>
              </div>
              <v-icon size="20" color="#A04DC2">mdi-shield-check-outline</v-icon>
            </header>

            <div class="permission-group__items">
              <v-checkbox
                v-for="perm in group.permissions"
                :key="perm.id"
                v-model="selectedPermissionIds"
                :value="perm.id"
                :label="perm.description"
                color="primary"
                dense
                hide-details
                @change="onPermissionChange(perm, group)"
              />
            </div>
          </section>
        </div>

        <div class="nd-form-actions">
          <v-btn type="button" text color="primary" @click="goBack">Cancelar</v-btn>
          <v-btn type="button" color="primary" elevation="0" :loading="loadingSave" @click="saveRole">
            Salvar cargo
          </v-btn>
        </div>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RoleDetails",

  data: () => ({
    page: {
      title: "",
      isNew: false,
    },

    role: {
      id: null,
      name: "",
      dentist: false,
    },

    availablePermissions: [],
    selectedPermissionIds: [],

    loadingPermissions: false,
    loadingSave: false,
    formValid: false,

    rules: {
      required: (value) => !!value || "Campo obrigatório.",
    },
  }),

  watch: {
    "$route.params.id": {
      immediate: true,
      handler(newId) {
        this.setPage();
        this.resetForm();

        if (!this.page.isNew && newId) {
          this.loadRole(newId);
        }
      },
    },
  },

  async created() {
    await this.loadAvailablePermissions();
  },

  methods: {
    setPage() {
      const id = this.$route.params.id;
      this.page.isNew = !id || id === "novo";
      this.page.title = this.page.isNew ? "Novo cargo" : "Detalhes do cargo";
    },

    resetForm() {
      this.role = { id: null, name: "", dentist: false };
      this.selectedPermissionIds = [];
      if (this.$refs.roleForm) this.$refs.roleForm.resetValidation();
    },

    onPermissionChange(perm, group) {
      if (!perm.key.endsWith(".*")) return;
      if (!this.selectedPermissionIds.includes(perm.id)) return;
      group.permissions.forEach((p) => {
        if (!this.selectedPermissionIds.includes(p.id)) {
          this.selectedPermissionIds.push(p.id);
        }
      });
    },

    async loadAvailablePermissions() {
      const excluded = ["users", "procedures", "sick_notes", "diseases"];
      this.loadingPermissions = true;
      try {
        const response = await axios.get("/roles/permissions");
        this.availablePermissions = (response.data.data || []).filter(
          (g) => !excluded.includes(g.prefix)
        );
      } catch (error) {
        console.error("Erro ao carregar permissões:", error);
        this.$dialog.notify.error("Erro ao carregar lista de permissões.", {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.loadingPermissions = false;
      }
    },

    async loadRole(id) {
      try {
        const response = await axios.get(`/roles/${id}`);
        const data = response.data.data;
        this.role = { id: data.id, name: data.name, dentist: !!data.dentist };
        this.selectedPermissionIds = (data.permissions || []).map((p) => p.id);
      } catch (error) {
        console.error("Erro ao carregar cargo:", error);
        this.$dialog.notify.error("Erro ao carregar detalhes do cargo.", {
          position: "top-right",
          timeout: 5000,
        });
        this.goBack();
      }
    },

    async saveRole() {
      if (!this.$refs.roleForm.validate()) return;

      const isNew = this.page.isNew;
      const confirm = await this.$dialog.confirm({
        title: "Cargo",
        text: isNew ? "Deseja salvar o cargo?" : "Deseja atualizar o cargo?",
        actions: { false: "Cancelar", true: isNew ? "Salvar" : "Atualizar" },
      });

      if (!confirm) return;

      this.loadingSave = true;
      try {
        const payload = {
          name: this.role.name,
          dentist: this.role.dentist,
          permissions: this.selectedPermissionIds,
        };

        if (isNew) {
          await axios.post("/roles", payload);
          this.$dialog.notify.success("Cargo criado com sucesso!", {
            position: "top-right",
            timeout: 3000,
          });
        } else {
          await axios.patch(`/roles/${this.role.id}`, payload);
          this.$dialog.notify.success("Cargo atualizado com sucesso!", {
            position: "top-right",
            timeout: 3000,
          });
        }

        this.goBack();
      } catch (error) {
        console.error("Erro ao salvar cargo:", error);
        this.$dialog.notify.error(this.$codes ? this.$codes(error) : "Erro ao salvar cargo.", {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.loadingSave = false;
      }
    },

    goBack() {
      this.$router.push("/cargos");
    },
  },
};
</script>

<style scoped lang="scss">
.role-form-grid {
  align-items: start;
}

.role-switch-panel {
  align-items: center;
  background: #f7f8f6;
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  min-height: 56px;
  padding: 12px 14px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111816;
    font-size: 13px;
  }

  span {
    color: #69736d;
    font-size: 12px;
    margin-top: 3px;
  }
}

.permissions-counter {
  background: #f5ecfa;
  border-radius: 999px;
  color: #a04dc2;
  font-size: 12px;
  font-weight: 700;
  padding: 7px 11px;
}

.permissions-loading {
  padding: 0 22px 24px;
}

.permissions-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 0 22px 24px;
}

.permission-group {
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  overflow: hidden;
}

.permission-group__header {
  align-items: center;
  background: #f7f8f6;
  border-bottom: 1px solid #dfe5e0;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111816;
    font-size: 14px;
  }

  span {
    color: #69736d;
    font-size: 12px;
    margin-top: 3px;
  }
}

.permission-group__items {
  display: grid;
  gap: 8px 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 14px 16px 16px;
}

@media only screen and (max-width: 960px) {
  .permissions-grid,
  .permission-group__items {
    grid-template-columns: 1fr;
  }

  .role-switch-panel {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
